import type { Editor } from "@tiptap/core";
import { ReactRenderer } from "@tiptap/react";
import type { SuggestionOptions, SuggestionProps } from "@tiptap/suggestion";
import tippy, { type Instance, type Props } from "tippy.js";
import { RecordMentionSuggestionMenu, type RecordMentionSuggestionMenuRef } from "./record-mention-suggestion-menu";
import type { RecordMentionItem } from "./types";

function mountOrUpdateMentionPopup({
  props,
  withLoading,
  component,
  popup,
  editor,
}: {
  props: SuggestionProps<RecordMentionItem>;
  withLoading: boolean;
  component: { current: ReactRenderer<RecordMentionSuggestionMenuRef> | undefined };
  popup: { current: Instance<Props>[] | undefined };
  editor: Editor;
}) {
  if (!props.clientRect) {
    return;
  }

  const mergedProps = { ...props, isLoading: withLoading };

  if (!component.current) {
    component.current = new ReactRenderer(RecordMentionSuggestionMenu, {
      props: mergedProps,
      editor,
    });

    popup.current = tippy("body", {
      getReferenceClientRect: props.clientRect,
      appendTo: () => document.body,
      content: component.current.element,
      showOnCreate: true,
      interactive: true,
      trigger: "manual",
      placement: "bottom-start",
    });
    return;
  }

  component.current.updateProps(mergedProps);

  if (!popup.current?.[0]) {
    return;
  }

  popup.current[0].setProps({
    getReferenceClientRect: props.clientRect,
  });
}

const renderRecordMentionOptions: SuggestionOptions["render"] = () => {
  const component: { current: ReactRenderer<RecordMentionSuggestionMenuRef> | undefined } = {
    current: undefined,
  };
  const popup: { current: Instance<Props>[] | undefined } = { current: undefined };

  return {
    onBeforeStart(props) {
      mountOrUpdateMentionPopup({
        props,
        withLoading: true,
        component,
        popup,
        editor: props.editor,
      });
    },

    onBeforeUpdate(props) {
      mountOrUpdateMentionPopup({
        props,
        withLoading: true,
        component,
        popup,
        editor: props.editor,
      });
    },

    onStart(props) {
      mountOrUpdateMentionPopup({
        props,
        withLoading: false,
        component,
        popup,
        editor: props.editor,
      });
    },

    onUpdate(props) {
      if (!component.current) {
        return;
      }

      component.current.updateProps({ ...props, isLoading: false });

      if (!props.clientRect || !popup.current?.[0]) {
        return;
      }
      popup.current[0].setProps({
        getReferenceClientRect: props.clientRect,
      });
    },

    onKeyDown(props) {
      if (props.event.key === "Escape") {
        popup.current?.[0]?.hide();
        return true;
      }

      return component.current?.ref?.onKeyDown(props) ?? false;
    },

    onExit() {
      popup.current?.[0]?.destroy();
      component.current?.destroy();
      component.current = undefined;
      popup.current = undefined;
    },
  };
};

export default renderRecordMentionOptions;
